/** @jsx jsx */
import { Button } from '@datacamp/waffles/button';
import { Code } from '@datacamp/waffles/code';
import { Copy } from '@datacamp/waffles/icon';
import { Paragraph } from '@datacamp/waffles/paragraph';
import { tokens } from '@datacamp/waffles/tokens';
import { css, jsx } from '@emotion/react';
import React, { ReactElement } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import ReactMarkdown from 'react-markdown';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import bash from 'react-syntax-highlighter/dist/cjs/languages/prism/bash';
import julia from 'react-syntax-highlighter/dist/cjs/languages/prism/julia';
import python from 'react-syntax-highlighter/dist/cjs/languages/prism/python';
import r from 'react-syntax-highlighter/dist/cjs/languages/prism/r';
import scala from 'react-syntax-highlighter/dist/cjs/languages/prism/scala';
import sql from 'react-syntax-highlighter/dist/cjs/languages/prism/sql';
import vs from 'react-syntax-highlighter/dist/cjs/styles/prism/vs';
import vscDarkPlus from 'react-syntax-highlighter/dist/cjs/styles/prism/vsc-dark-plus';

import { UiTheme } from './ThemeToggler';

SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('r', r);
SyntaxHighlighter.registerLanguage('sql', sql);
SyntaxHighlighter.registerLanguage('julia', julia);
SyntaxHighlighter.registerLanguage('scala', scala);
SyntaxHighlighter.registerLanguage('bash', bash);

type Props = {
  markdown: string;
  theme: UiTheme | 'ASSISTANT';
};

const MarkdownRenderer: React.FC<Props> = ({
  markdown,
  theme,
}): ReactElement => {
  const paragraphInverted = theme === 'DARK';
  const codeInverted = theme === 'DARK' || theme === 'ASSISTANT';

  return (
    <ReactMarkdown
      children={markdown}
      css={css`
        color: ${paragraphInverted ? tokens.colors.white : tokens.colors.navy};
      `}
      components={{
        p: ({ children }) => {
          return <Paragraph inverted={paragraphInverted}>{children}</Paragraph>;
        },
        code: ({ children, className, inline }) => {
          const language = className?.replace('language-', '');

          return !inline && language && typeof children[0] === 'string' ? (
            <SyntaxHighlighter
              language={language}
              customStyle={{
                backgroundColor: codeInverted
                  ? tokens.colors.navyLight
                  : tokens.colors.greyLight,
              }}
              style={codeInverted ? vscDarkPlus : vs}
              codeTagProps={{
                style: {
                  fontFamily: tokens.fontFamilies.mono,
                  backgroundColor: codeInverted
                    ? tokens.colors.navyLight
                    : tokens.colors.greyLight,
                  color: codeInverted
                    ? tokens.colors.white
                    : tokens.colors.navy,
                },
              }}
              children={children[0]}
            />
          ) : (
            <Code
              inverted={codeInverted}
              css={css`
                background-color: ${codeInverted
                  ? tokens.colors.navyLight
                  : tokens.colors.greyLight};
                color: ${codeInverted
                  ? tokens.colors.white
                  : tokens.colors.navy};
              `}
            >
              {children}
            </Code>
          );
        },
        pre: ({ children }) => {
          return (
            <div
              css={css`
                position: relative;
              `}
            >
              {children}
              {/* @ts-ignore */}
              <CopyToClipboard text={children[0].props.children}>
                <Button
                  variant="plain"
                  inverted={codeInverted}
                  icon={<Copy />}
                  aria-label="Copy Code"
                  css={css`
                    position: absolute;
                    inset-block-start: ${tokens.spacing.xsmall};
                    inset-inline-end: ${tokens.spacing.xsmall};
                  `}
                />
              </CopyToClipboard>
            </div>
          );
        },
      }}
    />
  );
};

export default MarkdownRenderer;
